import { useState } from "react";
import type { AssignmentResultRow } from "./access-client";
import { useGoneLocations, withoutIds } from "./reconcile-locations";
import { useAssignMemberLocations } from "./use-access-mutations";
import { useAccessSubmit } from "./use-access-submit";
import { useMemberLocations } from "./use-member-locations";
import { useRemoveSelection } from "./use-remove-selection";
import { withToggled } from "./toggle-set";

interface ManageLocationsStateInput {
  readonly organizationId: string;
  readonly membershipId: string;
  readonly userId: string;
  readonly onDone: (results: readonly AssignmentResultRow[]) => void;
  readonly onStale: (message: string) => void;
  readonly onOpenChange: (open: boolean) => void;
}

/**
 * Everything the manage-locations dialog decides: what is ticked on each side,
 * the names it can still show for ticked rows on other pages, what the member
 * holds today, and the submit. The ticks to remove are always cut down to the
 * member's active locations as last read, so a location that vanished (or was
 * removed by someone else) can never be sent again.
 */
export function useManageLocationsState({
  organizationId,
  membershipId,
  userId,
  onDone,
  onStale,
  onOpenChange,
}: ManageLocationsStateInput) {
  const assign = useAssignMemberLocations();
  const current = useMemberLocations(userId, membershipId, true);
  const goneLocations = useGoneLocations(organizationId);
  const [toAdd, setToAdd] = useState<ReadonlySet<string>>(new Set());
  const [pickedNames, setPickedNames] = useState<ReadonlyMap<string, string>>(new Map());
  const [results, setResults] = useState<readonly AssignmentResultRow[] | null>(null);

  const { activeIds, toRemove, names, toggleRemove, clearRemove } = useRemoveSelection(
    current.locations,
    pickedNames,
  );

  const currentReady = !current.isLoading && !current.isError;
  const nothingToSend = toAdd.size === 0 && toRemove.size === 0;
  const removesEverything =
    toAdd.size === 0 &&
    toRemove.size > 0 &&
    activeIds.length > 0 &&
    activeIds.every((id) => toRemove.has(id));

  const { submit, error, handleOpenChange } = useAccessSubmit({
    run: () => {
      goneLocations.snapshot();
      return assign.mutateAsync({
        organizationId,
        membershipId,
        add: [...toAdd],
        remove: [...toRemove],
      });
    },
    isPending: assign.isPending,
    canSubmit: currentReady && !nothingToSend,
    onSuccess: (result) => {
      setResults(result.results);
      onDone(result.results);
    },
    onStale,
    onOpenChange,
    onLocationGone: async () => {
      const gone = await goneLocations.find();
      setToAdd((prev) => withoutIds(prev, gone));
      // The API validates the ids to remove against the organization too, and a
      // location that is gone may still be listed on the member: start the remove side over.
      clearRemove();
    },
  });

  return {
    current,
    isPending: assign.isPending,
    toAdd,
    toRemove,
    names,
    activeIds,
    removesEverything,
    nothingToSend,
    canSubmit: currentReady && !nothingToSend && !assign.isPending,
    results,
    error,
    submit,
    handleOpenChange,
    toggleAdd: (id: string, name: string) => {
      setPickedNames((prev) => new Map(prev).set(id, name));
      setToAdd((prev) => withToggled(prev, id));
    },
    toggleRemove,
  };
}
